import { StickyHeaderWrapper } from '@portal/shared/components/StickyHeaderWrapper';
import { AlternateLinks } from '@portal/shared/layouts/AlternateLinks';
import { CanonicalLink } from '@portal/shared/layouts/CanonicalLink';
import { ProductDetailPageWrapper } from '@portal/shared/studio/ProductDetailPage/ProductDetailPageWrapper/ProductDetailPageWrapper';
import { getVariantsPerStore } from '@portal/shared/studio/ProductDetailPage/utils';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useProductStore } from '@portal/state/store/pdpStore';
import { InferGetStaticPropsType } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { PageProvider, type PageContextProps } from 'processes/Page/PageContext';
import { SHOPIFY_METAFIELDS_IDENTIFIERS } from 'processes/ShopListBundles/constants';
import { useShopListBundleCollectionQuery } from 'processes/ShopListBundles/hooks/useShopListBundlesCollections';
import { useEffect, useMemo } from 'react';
import { getActiveBannerForHeader } from 'shared/utils/bannerVisibility';
import { applyBannerReplacements } from 'shared/utils/contentReplacements';
import { getCanonicalLink } from 'shared/utils/getCanonicalLink';
import { urlFor } from 'shared/utils/urlFor';
import { useCloudfrontLocationQuery } from 'state/hooks/useCloudfrontLocation';
import { PageResult, PageSection, PDPSection } from 'types/studio/page';
import { createVisibleOnPageLoadCalculator } from './createVisibleOnPageLoadCalculator';
import type { getStaticProps } from './getStaticProps';
import { renderSection } from './renderSection';
import { isSectionType } from './utils';

const GladlyChat = dynamic(() => import('shared/components/GladlyChat/GladlyChat'), {});
const Footer = dynamic(() => import('shared/studio/Footer/Footer'), { ssr: true });

const replaceBuildABundleLinks = (data: unknown, replacement?: string): unknown => {
  if (!replacement) return data;

  if (typeof data === 'string') {
    // Check if it's a URL string containing either path
    if (data.includes('/build-a-bundle') || data.includes('/get-your-ag1')) {
      // If it's an absolute URL (starts with http or https)
      if (data.match(/^https?:\/\//)) {
        const url = new URL(data);
        return url.origin + replacement;
      }
      // If it's a relative URL, just use the replacement as is
      return replacement;
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => replaceBuildABundleLinks(item, replacement));
  }

  if (typeof data === 'object' && data !== null) {
    const newObj: Record<string, unknown> = {};
    Object.keys(data as object).forEach((key) => {
      newObj[key] = replaceBuildABundleLinks((data as Record<string, unknown>)[key], replacement);
    });
    return newObj;
  }

  return data;
};

const isHeaderSection = (section: PageSection) =>
  isSectionType(section, ['headerFull', 'headerFullV2', 'headerLanderV3']);

export const ContentPage = ({
  pageData: initialPageData,
  footerData,
  headerData: initialHeaderData,
  bannerData,
  locale,
  preview,
  usedProductsMap,
  productsInventory,
  pageTranslationsLinks,
  jobs
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const currentStore = useStoreValue();

  const { setVariants, setBundleVariant, setBundleData, resetProductState, setPdpType } =
    useProductStore();

  const router = useRouter();
  const { data: cloudfrontLocation } = useCloudfrontLocationQuery();

  const activeBanner = useMemo(
    () =>
      getActiveBannerForHeader({
        bannerData: bannerData || [],
        country: cloudfrontLocation?.country,
        path: router.asPath
      }),
    [bannerData, router.asPath, cloudfrontLocation]
  );

  const { getHeaderDataDefaults, useSetHeaderOptions } = useHeaderContext();
  useSetHeaderOptions(initialHeaderData);

  // Transform headerData
  const headerData = useMemo(() => {
    const result = getHeaderDataDefaults(initialHeaderData);
    if (!result || !activeBanner?.globalBabReplace) {
      return result;
    }

    return replaceBuildABundleLinks(result, activeBanner.globalBabReplace) as typeof result;
  }, [activeBanner?.globalBabReplace, getHeaderDataDefaults, initialHeaderData]);

  // Transform pageData
  const pageData = useMemo(() => {
    if (!activeBanner) return initialPageData;

    // Create deep copy of pageData to avoid mutations
    const newPageData: PageResult = JSON.parse(JSON.stringify(initialPageData));

    newPageData.sections = (newPageData.sections || []).map((section: any) => {
      // First apply general banner replacements if we have a valid banner
      const updatedSection = activeBanner
        ? applyBannerReplacements(section, activeBanner)
        : section;

      // Then handle specific build-a-bundle URL replacements
      if (activeBanner?.globalBabReplace) {
        if (updatedSection.marketingButton?.marketingLink) {
          updatedSection.marketingButton = {
            ...updatedSection.marketingButton,
            marketingLink: replaceBuildABundleLinks(
              updatedSection.marketingButton.marketingLink,
              activeBanner.globalBabReplace
            ) as string
          };
        }

        if (updatedSection.unifiedButton?.bundleUrl) {
          updatedSection.unifiedButton = {
            ...updatedSection.unifiedButton,
            bundleUrl: replaceBuildABundleLinks(
              updatedSection.unifiedButton.bundleUrl,
              activeBanner.globalBabReplace
            ) as string
          };
        }

        if (updatedSection.unifiedButton?.baseButtonLinkV2?.btnLink?.external) {
          updatedSection.unifiedButton = {
            ...updatedSection.unifiedButton,
            baseButtonLinkV2: {
              ...updatedSection.unifiedButton.baseButtonLinkV2,
              btnLink: {
                ...updatedSection.unifiedButton.baseButtonLinkV2.btnLink,
                external: replaceBuildABundleLinks(
                  updatedSection.unifiedButton.baseButtonLinkV2.btnLink.external,
                  activeBanner.globalBabReplace
                ) as string
              }
            }
          };
        }
      }

      return updatedSection;
    });

    return newPageData;
  }, [initialPageData, activeBanner]);

  const {
    pdp,
    seo,
    sections,
    structuredSchemaData,
    disableFooter,
    footerShopMenus,
    hasDifferentFooterLinks,
    footerLogoLink
  } = pageData;

  // initialize pdp state and get variants for the current store
  const {
    showPDP,
    pdpSectionsLeft,
    pdpSectionsRight,
    pdpType,
    variants,
    bundleVariant,
    otpVariant
  } = pdp ?? {};

  const isProductPdP = pdpType === 'productView';
  const isBundlePdP = pdpType === 'bundleView';
  const isOtpPdP = pdpType === 'otpView';

  // fetch shopify collection data
  const collectionHandle =
    isBundlePdP && bundleVariant?.length === 1 ? bundleVariant[0].bundleCollectionHandle : '';

  const { data: bundleData } = useShopListBundleCollectionQuery({
    storeCode: currentStore,
    collectionHandle,
    identifiers: SHOPIFY_METAFIELDS_IDENTIFIERS,
    isEnabled: Boolean(collectionHandle)
  });

  useEffect(() => {
    setPdpType(pdpType || 'productView');
  }, [pdpType, setPdpType]);

  // Effect to set up product data
  useEffect(() => {
    let variantsForStore = null;

    if (isProductPdP && variants?.length) {
      // initialize pdp state and get variants for the current store
      variantsForStore = getVariantsPerStore(variants as any, currentStore);
    } else if (isOtpPdP && otpVariant?.length) {
      variantsForStore = getVariantsPerStore(otpVariant as any, currentStore);
    }

    setVariants(variantsForStore ?? null);

    if (isBundlePdP && bundleVariant && bundleData) {
      setBundleVariant(bundleVariant?.[0]);
      setBundleData(bundleData);
    }
  }, [
    pdpType,
    variants,
    otpVariant,
    bundleVariant,
    bundleData,
    currentStore,
    setVariants,
    setBundleData,
    setBundleVariant,
    resetProductState,
    isProductPdP,
    isBundlePdP,
    isOtpPdP
  ]);

  const sectionData = useMemo(
    () => ({
      preview,
      headerData,
      pageTranslationsLinks,
      jobs,
      usedProductsMap,
      productsInventory,
      activeBanner
    }),
    [
      preview,
      headerData,
      pageTranslationsLinks,
      jobs,
      usedProductsMap,
      productsInventory,
      activeBanner
    ]
  );

  const fullHeader = useMemo(
    () => sections.find((item) => item._type === 'headerFullV2'),
    [sections]
  );

  const sectionsUsed = useMemo(() => {
    if (!sections.length) {
      return null;
    }

    const result: PageContextProps['sectionsUsed'] = {
      sanityOffer: false,
      builderOffer: false,
      offerAnchor: false
    };

    for (let i = 0; i < sections.length; i += 1) {
      const section = sections[i];
      if (!result.sanityOffer && section._type.startsWith('offer')) {
        result.sanityOffer = true;
      }

      if (
        !result.navigation &&
        fullHeader?._type &&
        section._type !== fullHeader._type &&
        /^(header)\w+/.test(section._type)
      ) {
        result.navigation = true;
      }

      if (result.sanityOffer && result.navigation) {
        break;
      }
    }

    return result;
  }, [fullHeader?._type, sections]);

  const gladlyElement = useMemo(() => {
    const isGladlyNeeded = sections.some((item) =>
      isSectionType(item, ['heroCustomer', 'imageAndTextV3'])
    );

    return isGladlyNeeded ? <GladlyChat /> : null;
  }, [sections]);

  const headerElement = useMemo(() => {
    const children = sections
      .filter(isHeaderSection)
      .map((section) => renderSection(section, { ...sectionData, visibleOnPageLoad: true }))
      .filter(Boolean);

    if (!children.length) {
      return null;
    }

    if (sectionsUsed?.navigation) {
      return <StickyHeaderWrapper>{children}</StickyHeaderWrapper>;
    }

    return <div>{children}</div>;
  }, [sections, sectionData, sectionsUsed?.navigation]);

  const pdpElement = useMemo(() => {
    const renderPDPSections = (items: PDPSection[] | undefined) =>
      items?.map((section) => renderSection(section, sectionData));

    return showPDP ? (
      <ProductDetailPageWrapper
        leftSections={renderPDPSections(pdpSectionsLeft)}
        rightSections={renderPDPSections(pdpSectionsRight)}
      />
    ) : null;
  }, [sectionData, showPDP, pdpSectionsLeft, pdpSectionsRight]);

  const bodyElements = useMemo(() => {
    const visibleOnPageLoadCalculator = createVisibleOnPageLoadCalculator();

    return sections
      .filter((item) => !isHeaderSection(item))
      .map((section) =>
        renderSection(section, {
          ...sectionData,
          visibleOnPageLoad: visibleOnPageLoadCalculator({ section })
        })
      );
  }, [sections, sectionData]);

  const footerSettings = useMemo(() => {
    return {
      shopLinks: footerShopMenus,
      hasDifferentLinks: hasDifferentFooterLinks,
      logoLink: footerLogoLink
    };
  }, [footerShopMenus, hasDifferentFooterLinks, footerLogoLink]);

  if (!seo || !sections.length) {
    return null;
  }

  const localCanonicalLink = pageData.canonical?.internal?.slug.current || pageData.slug.current;
  const externalCanonical = pageData.canonical?.external;
  const canonicalLink = externalCanonical || getCanonicalLink(localCanonicalLink, locale);

  return (
    <PageProvider
      page={pageData}
      baseOfferSubscriptionTypes={null}
      baseOfferAddonTypes={null}
      shopifyVariantsByStoreCode={null}
      sectionsUsed={sectionsUsed}>
      <Head>
        <title>{seo.title || 'AG1 by Athletic Greens® - Official Site'}</title>
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={seo.title || 'AG1 by Athletic Greens® - Official Site'}
        />
        {seo.description && (
          <>
            <meta name="description" content={seo.description} />
            <meta property="og:description" content={seo.description} />
          </>
        )}
        {seo.image && <meta property="og:image" content={seo.image && urlFor(seo.image).url} />}
        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        {seo.noindex && <meta name="robots" content="noindex,nofollow" />}
        {structuredSchemaData?.map((schema: { jsonSchemaData: string; schemaType: string }) => (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: schema.jsonSchemaData
            }}
            key={schema.schemaType}
          />
        ))}
      </Head>
      <CanonicalLink href={canonicalLink} />
      <AlternateLinks translations={pageData.translations} />
      <main data-content="main">
        {headerElement}
        {gladlyElement}
        {pdpElement}
        {bodyElements}
      </main>
      {footerData && footerSettings && !disableFooter && (
        <Footer
          data={footerData}
          pageTranslations={pageTranslationsLinks}
          footerSettings={footerSettings}
        />
      )}
    </PageProvider>
  );
};
